import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { DimensionamentoService } from "../../services/DimensionamentoServices";
import NeedsByServiceTypeTableReport from "../TableReport/NeedsByServiceTypeTableReport";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Swal from "sweetalert2";
import LoadingAnimation from "../Loading/LoadingAnimation";

const NeedsByServiceTypeReport = ({ returnContext }) => {
  const location = useLocation();
  const [selectedCity, setSelectedCity] = useState("Nan");
  const [cityData, setCityData] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [openAll, setOpenAll] = useState(false);
  const [search, setSearch] = useState("");
  const [causeAndActionReportData, setCauseAndActionReportData] = useState([]);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  useEffect(() => {
    if (cityData.length > 0) {
      if (returnContext?.selectedCity) {
        const cityExists = cityData.some(city => city.city === returnContext.selectedCity);
        if (cityExists) {
          setSelectedCity(returnContext.selectedCity);
        }
      } else if (location.state?.returnContext) {
        const { selectedCity: returnCity } = location.state.returnContext;
        if (returnCity && returnCity !== "Nan") {
          const cityExists = cityData.some(city => city.city === returnCity);
          if (cityExists) {
            setSelectedCity(returnCity);
          }
          
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    }
  }, [location.state, returnContext, cityData]);

  useEffect(() => {
    const fetchCities = async () => {
      const cities = await DimensionamentoService.fetchCities();
      setCityData(cities);
    };
    fetchCities();
  }, []);

  useEffect(() => {
    const groups = [
      { ids: "1.1", name: "Urgência e Emergência" },
      { ids: "1.2", name: "Especialidades médicas ambulatoriais" },
      { ids: "2.1", name: "Especialidades médicas ambulatoriais por área de atuação" },
      { ids: "2.2", name: "Pediátria por área de atuação" },
      { ids: "3.1", name: "Especialidade de Profissionais Não Médicos Ambulatoriais" },
      { ids: "4.1", name: "Serviço de apoio diagnóstico e terapias ambulatoriais - Medicina Laboratorial" },
      { ids: "4.2", name: "Serviço de apoio diagnóstico e terapias ambulatoriais - Métodos diagnóstico por imagem" },
      { ids: "4.3", name: "Procedimentos Ambulatoriais" },
      { ids: "5.1", name: "Leitos de Internação geral" },
      { ids: "5.2", name: "Leitos de Internação em UTI" },
    ];

    const fetchData = async () => {
      const cityObj = cityData.find((city) => city.city === selectedCity);

      if (cityObj && cityObj.city && cityObj.state) {
        setIsLoadingReport(true);

        try {
          const res = await DimensionamentoService.getNeedsByServiceTypeReport(
            cityObj.city,
            cityObj.state
          );
          const causeAndActionRes =
            await DimensionamentoService.getCauseAndActionReport(
              cityObj.city,
              cityObj.state
            );

          const groupedMap = {};

          for (const item of res) {
            const hasNaNValue = [
              item.availableProvidersCity,
              item.networkAjustmentFactor,
              item.expectedProvidersNeedsCity,
            ].some(isNaN);

            if (hasNaNValue) {
              console.error(
                "Erro: Valores NaN encontrados no relatório após a requisição:",
                item
              );

              await Swal.fire({
                icon: "error",
                title: "Erro",
                text: "Ocorreu um erro ao carregar os dados do relatório. Tente novamente e, se o problema persistir, entre em contato com um administrador.",
                confirmButtonText: "OK",
              });

              window.location.reload();
              return;
            }

            const prefix = item.parameterCode.split(".")[0] + "." + item.parameterCode.split(".")[1];

            if (!groupedMap[prefix]) groupedMap[prefix] = [];

            groupedMap[prefix].push({
              ...item,
              reportGroup: groups.find(g => g.ids === prefix)?.name || "Outros",
            });
          }

          const orderedReportData = groups
            .map(g => groupedMap[g.ids])
            .filter(Boolean);

          setReportData(orderedReportData);
          setCauseAndActionReportData(causeAndActionRes);

        } catch (error) {
          console.error("Erro ao carregar os dados do backend:", error);
        } finally {
          setIsLoadingReport(false);
        }
      } else {
        setReportData([]);
        setCauseAndActionReportData([]);
        setIsLoadingReport(false);
      }
    };

    fetchData();
  }, [selectedCity, cityData]);

  return (
    <div className="w-full">
      <h1 className="text-lg font-medium text-gray-700 text-center">
        Selecione um município
      </h1>
      <div className="flex flex-col justify-center items-center py-4 gap-4">
        <FormControl variant="outlined" sx={{ width: 350 }}>
          <InputLabel>Município</InputLabel>
          <Select
            variant="outlined"
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            label="Município"
          >
            <MenuItem value="Nan">Selecione um município</MenuItem>
            {cityData
              .slice()
              .sort((a, b) => a.city.localeCompare(b.city, "pt-BR"))
              .map((c, i) => (
                <MenuItem key={i} value={c.city}>
                  {c.city}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        {selectedCity !== "Nan" && (
          <div className="flex flex-col sm:flex-row justify-center items-center gap-2 w-full">
            <FormControl fullWidth sx={{ m: 1 }}>
              <TextField
                rows={1}
                id="searchField"
                label="Pesquise por Código, Tipo Prestador ou Nome do Atributo"
                onChange={(event) => setSearch(event.target.value)}
                value={search}
              />
            </FormControl>
            {openAll ? (
              <button
                className="bg-red-700 hover:bg-red-900 text-white px-4 rounded-md min-w-36 h-14 shadow-md w-full lg:w-auto lg:mt-0 mt-2 mx-2"
                onClick={() => {
                  setOpenAll(!openAll);
                }}
              >
                Fechar todos
              </button>
            ) : (
              <button
                className="bg-emerald-700 hover:bg-emerald-900 text-white px-4 rounded-md min-w-36 h-14 shadow-md w-full lg:w-auto lg:mt-0 mt-2 mx-2"
                onClick={() => {
                  setOpenAll(!openAll);
                }}
              >
                Abrir todos
              </button>
            )}
          </div>
        )}
      </div>

      <div className="pt-5 w-full h-full space-y-6 pb-6">
        {selectedCity !== "Nan" ? (
          isLoadingReport ? (
            <LoadingAnimation />
          ) : (
            reportData.map((group, index) => (
              <NeedsByServiceTypeTableReport
                key={index}
                title={`${group[0].reportGroup}`}
                group={group}
                city={selectedCity}
                state={
                  cityData.find((city) => city.city === selectedCity)?.state
                }
                cityObj={cityData.find((city) => city.city === selectedCity)}
                openAll={openAll}
                search={search}
                causeAndActionReportData={causeAndActionReportData}
              />
            ))
          )
        ) : (
          <div className="flex justify-center items-center h-full">
            <h1 className="text-lg font-medium text-gray-700">
              Escolha um município
            </h1>
          </div>
        )}
      </div>
    </div>
  );
};

export default NeedsByServiceTypeReport;
